import React from "react";
import { Link } from "react-router-dom";
import { Trash2 } from "lucide-react";
import { Logo } from "@/components/Logo";

const SUPPORT_EMAIL = "admin@debiiapp.com";

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-20" data-testid={`delete-section-${id}`}>
      <h2 className="font-display text-lg md:text-xl font-bold text-foreground mt-8 mb-3">{title}</h2>
      <div className="space-y-3 text-sm leading-relaxed text-muted-foreground">{children}</div>
    </section>
  );
}

export default function DeleteAccount() {
  return (
    <div className="min-h-screen bg-background" data-testid="delete-account-page">
      <header className="sticky top-0 z-10 border-b border-border bg-background/90 backdrop-blur">
        <div className="max-w-2xl mx-auto px-5 h-14 flex items-center justify-between">
          <Link to="/" data-testid="delete-home-link" aria-label="Debii home">
            <Logo size={30} />
          </Link>
          <span className="text-xs font-semibold text-muted-foreground">Delete Account</span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 pb-20">
        <div className="mt-8 rounded-2xl bg-primary text-primary-foreground p-6">
          <div className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-emerald-300" />
            <p className="font-display font-bold text-lg">Delete Your Debii Account</p>
          </div>
          <p className="mt-2 text-sm text-white/85 leading-relaxed">
            You can permanently delete your Debii account and its data at any time, directly from the app.
            This page explains how.
          </p>
        </div>

        <Section id="how-to-delete" title="How to delete your account">
          <p>You can delete your account yourself in a few steps:</p>
          <ol className="list-decimal pl-5 space-y-2">
            <li>
              Sign in at{" "}
              <a className="text-primary font-medium" href="https://debiiapp.com" data-testid="delete-signin-link">
                https://debiiapp.com
              </a>.
            </li>
            <li>Go to <strong className="text-foreground">Settings → Account</strong>.</li>
            <li>Select <strong className="text-foreground">&ldquo;Delete my account&rdquo;</strong>.</li>
            <li>Enter your password to confirm deletion.</li>
          </ol>
        </Section>

        <Section id="what-is-deleted" title="What gets deleted">
          <p>
            Deleting your account is <strong className="text-foreground">permanent and cannot be undone</strong>. When
            you confirm deletion, we permanently remove your account along with the debt records and related data
            associated with it, including the people, amounts, repayment history, reminders and acknowledgement records
            you created.
          </p>
          <p>Once deleted, this information cannot be recovered, and you will need to create a new account to use Debii again.</p>
        </Section>

        <Section id="retention" title="Information we may retain">
          <p>
            In some cases, limited information may be retained where we are legally required to keep it, or for
            legitimate purposes such as meeting legal, accounting or regulatory obligations, resolving disputes and
            preventing abuse. Any such retention is handled in accordance with our{" "}
            <Link to="/privacy-policy" className="text-primary font-medium" data-testid="delete-privacy-link">
              Privacy Policy
            </Link>.
          </p>
        </Section>

        <Section id="help" title="Can't access your account?">
          <p>
            If you are unable to sign in to delete your account yourself, contact our support team and we will help you:
          </p>
          <p>
            <a className="text-primary font-medium" href={`mailto:${SUPPORT_EMAIL}`} data-testid="delete-contact-email">
              {SUPPORT_EMAIL}
            </a>
          </p>
          <p>
            See also our{" "}
            <Link to="/privacy-policy" className="text-primary font-medium" data-testid="delete-privacy-link-footer">
              Privacy Policy
            </Link>.
          </p>
        </Section>
      </main>
    </div>
  );
}
